import type { Alternative, AlternativesRequest } from '@lyrmo/shared';
import { useMemo, useState } from 'react';
import type { RewriteClient } from '../../api/client';

type Rewrite = Pick<AlternativesRequest, 'original' | 'rewrite'>;

export interface Suggest {
  (request: AlternativesRequest): Promise<Alternative[]>;
  /** The other ways already in hand for a rewrite, so they show at once, without a wait. */
  readonly known?: (rewrite: Rewrite) => readonly Alternative[] | undefined;
}

const keyOf = ({ original, rewrite }: Rewrite) => JSON.stringify([original, rewrite]);

/**
 * Asks for other ways to say the changes in a rewrite, once: switching views, or back to a card,
 * reuses the answer. Failures aren't kept, so trying again asks afresh. Undefined where the
 * client can't ask.
 */
export function useAlternatives(client: RewriteClient): Suggest | undefined {
  const [cache] = useState(() => new Map<string, Promise<Alternative[]>>());
  const [answers] = useState(() => new Map<string, Alternative[]>());
  const { suggestAlternatives } = client;

  return useMemo(() => {
    if (!suggestAlternatives) return undefined;
    const suggest = (request: AlternativesRequest) => {
      const key = keyOf(request);
      const cached = cache.get(key);
      if (cached) return cached;
      const pending = suggestAlternatives(request);
      cache.set(key, pending);
      pending.then(
        (alternatives) => answers.set(key, alternatives),
        () => cache.delete(key),
      );
      return pending;
    };
    return Object.assign(suggest, {
      known: (rewrite: Rewrite) => answers.get(keyOf(rewrite)),
    });
  }, [answers, cache, suggestAlternatives]);
}
